import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterStateSnapshot } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Observable } from 'rxjs';
import { BillingOrder } from 'src/app/models/billingorder.model';
import { PendingOrderService } from 'src/app/services/pendingorder.service';
import { ScrBaseComponent } from '../../base.component';
import { ShortCodesRole, OrderTransactionType } from '../../../models/constants';

export interface IncomingState {
  navigationId: number;
  payLater: boolean;
  manualPay: boolean;
  bulk: boolean;
  renew: boolean;
  needsactionRenew: boolean;
  order: BillingOrder;
}


@Component({
  selector: 'app-order-short-code',
  templateUrl: './order-short-code.component.html',
  styleUrls: ['./order-short-code.component.scss'],
})
export class OrderShortCodeComponent extends ScrBaseComponent implements OnInit {

  protected skipUrls: string[] = [
    '/dashboard/(navbar-tab:order-code/(order-code-tab:application))',
    '/dashboard/(navbar-tab:order-code/(order-code-tab:cart))',
    '/dashboard/(navbar-tab:order-code/(order-code-tab:payment))',
    '/dashboard/(navbar-tab:thank-you)'
  ];

  CognitoGroup: typeof ShortCodesRole = ShortCodesRole;
  keepTheCode = false;
  payLater = false;
  disableApplicationTab!: boolean;
  manualPayForMonthlyBilling = false;
  bulkRenew = false;
  renew = false;

  @ViewChild(ConfirmDialog, { static: false }) confirmDialog!: ConfirmDialog;

  constructor(
    protected pendOrderService: PendingOrderService,
    protected confirmationService: ConfirmationService,
    protected router: Router,
    protected activatedRoute: ActivatedRoute) {

    super();

    /**
     * NOTE: when navigating between tabs on the checkout pages, this component is not re-created.
     * We create a subscription on router events and get the snapshot of the activated route
     * to know if the route data tells us to disable or enable the application tab.
     * At the moment, it is enabled for the Cart page but not otherwise.
     */

    // give it an initial value when we are constructed.
    this.disableApplicationTab = this.activatedRoute.snapshot?.firstChild?.data?.disableAppTab;

    // subscribe 
    this.subscriptions.push(this.router.events.subscribe({
      next: (event) => {
        if (event instanceof NavigationEnd) {
          const dat = this.activatedRoute.firstChild?.snapshot.data['disableAppTab'];
          this.disableApplicationTab = dat;
        }
      }
    }));

    this.manualPayForMonthlyBilling = this.isManualPay();
  }

  ngOnInit(): void {
    if (history.state['manualPay'] || this.isManualPay()) { this.manualPayForMonthlyBilling = true; }
    if (history.state['renew'] || this.isRenew()) { this.renew = true; }
    if (history.state['payLater'] || this.isPayLater()) { this.payLater = true; }
    if (history.state['bulk'] || this.isBulkRenew()) { this.bulkRenew = true; }
  }

  // Overwrite it here to handle the data
  canDeactivate(
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot
  ): boolean | Observable<boolean> {

    // the current navigation can tell us to save the state or not.
    const saveState: boolean = this.router.getCurrentNavigation()?.extras?.state?.saveState;
    if (saveState !== undefined) {
      this.pendOrderService.saveOrder(saveState);
    }

    const unattended: boolean = this.router.getCurrentNavigation()?.extras?.state?.saveState;
    if (unattended) {
      return true;
    }

    const nextURL = currentState?.url;
    console.log('next URL ' + nextURL);

    // if we are leaving the cart / thank you pages, we may need to ask
    // user to save their cart.
    if (!(this.skipUrls.indexOf(nextURL) > -1)) {
      const transactionType = this.getOrderTransactionType();
      if (transactionType === OrderTransactionType.New) {
        const pendingOrder = this.pendOrderService.getPendingOrder();
        if ( // if pay later no point showing keep the codes
          pendingOrder &&
          pendingOrder.leases &&
          pendingOrder.leases.length > 0
        ) {

          // we cannot answer right away so return an observable that gets
          // completed when the user selects a button on the dialog.
          return new Observable((o) => {
            this.leaveCart('scr.leavecart.order').then((b) => {
              o.next(b);
              o.complete();
            });
          });
        }
      }
      else {
        // user is leaving the cart and this is NOT a new order.  Update the saved cart
        // and don't save when the individual page is destroyed.
        this.pendOrderService.setOrderTransactionDetails(OrderTransactionType.New, new BillingOrder());
        this.pendOrderService.saveOrder(false);
        return true;
      }
    }

    return true;
  }

  //Only used for new order
  protected leaveCart(thingKey: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.translate.get(thingKey).subscribe((thing) => {
        this.translate
          .get(['scr.leavecart', 'scr.leavecart.title'], { thing })
          .subscribe((items) => {
            this.confirmationService.confirm({
              key: 'leavecart',
              header: items['scr.leavecart.title'],
              message: items['scr.leavecart'],
              accept: () => {
                if (this.keepTheCode) {
                  this.pendOrderService.saveOrder(true);
                } else {
                  this.pendOrderService.pricingHash = '';
                  this.pendOrderService.removeAllOrder(OrderTransactionType.New);
                  this.pendOrderService.saveOrder(false);
                }
                resolve(true);
              },
              reject: () => {
                resolve(false);
              },
            });
            window.scrollTo(0, 0);
          });
      });
    });
  }

  setKeepTheCode(): void {
    this.keepTheCode = true;
  }

  unSetKeepTheCode(): void {
    this.keepTheCode = false;
  }


  isPayLater(): boolean {
    const ott = this.getOrderTransactionType();
    const plFlag = ott === OrderTransactionType.NewPayLater ||
      ott === OrderTransactionType.RenewPayLater ||
      ott === OrderTransactionType.Renew1Plus12PayLater;
    return plFlag;
  }

  isManualPay(): boolean {
    return this.getOrderTransactionType() === OrderTransactionType.ManualPayMonthlyBilling;
  }

  isRenew(): boolean {
    const ott = this.getOrderTransactionType();
    const renew = ott === OrderTransactionType.Renew ||
      ott === OrderTransactionType.RenewPayLater ||
      ott === OrderTransactionType.Renew1Plus12PayLater ||
      ott === OrderTransactionType.EnrollInMonthlyBilling ||
      ott === OrderTransactionType.Renew1Plus12 ||
      ott === OrderTransactionType.Renew1Plus12AfterDeadline;
    return renew;
  }

  isBulkRenew(): boolean {
    return this.getOrderTransactionType() === OrderTransactionType.BulkRenew;
  }

  isNewOrder(): boolean {
    return this.getOrderTransactionType() === OrderTransactionType.New;
  }

  getOrderTransactionType(): OrderTransactionType | undefined {
    return this.pendOrderService.orderTransaction$.value.transactionType;
  }

  getDisableApplication(): boolean {
    return this.disableApplicationTab;
  }

}
